import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GameDetails } from '../../components/GameDetails';
import { Game, Participant, ParticipationStatus } from '../../types/game';
import { gameService } from '../../services/gameService';
import { toastManager } from '../../lib/toastManager';
import { shareGame } from '../../utils/shareUtils';

export default function GameDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [game, setGame] = useState<Game | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const token = searchParams.get('token');

  // Função para carregar dados do jogo
  const fetchGameDetails = async () => {
    if (!id) {
      setError('ID do jogo não fornecido');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      console.log('🎯 Fetching game details:', { id, token });
      const gameData = await gameService.getGameById(id, token || undefined);
      console.log('✅ Game details fetched:', gameData);
      setGame(gameData);
      
      // Carregar participantes após carregar o jogo
      await fetchParticipants(id, true);
    } catch (error) {
      console.error('❌ Error fetching game details:', error);
      setError(error instanceof Error ? error.message : 'Failed to load game details');
    } finally {
      setLoading(false);
    }
  };

  // Função para carregar participantes
  const fetchParticipants = async (gameId: string, showLoading = false) => {
    try {
      if (showLoading) {
        setLoadingParticipants(true);
      }
      console.log('👥 Fetching participants for game:', gameId);
      const participantsData = await gameService.getGameParticipants(gameId);
      console.log('✅ Participants fetched:', participantsData);
      setParticipants(participantsData);
    } catch (error) {
      console.error('❌ Error fetching participants:', error);
      // Don't set error for participants, just log it
      setParticipants([]);
    } finally {
      if (showLoading) {
        setLoadingParticipants(false);
      }
    }
  };

  // Função para recarregar dados completos sem loading visível
  const refreshGameData = async () => {
    try {
      console.log('🔄 Refreshing game data silently');
      const [gameData, participantsData] = await Promise.all([
        gameService.getGameById(id!, token || undefined),
        gameService.getGameParticipants(id!)
      ]);
      
      // Atualizar tudo de uma vez para evitar múltiplos re-renders
      setGame(gameData);
      setParticipants(participantsData);
      
      console.log('✅ Game data refreshed');
    } catch (error) {
      console.error('❌ Error refreshing game data:', error);
      // Não mostrar erro, apenas manter dados atuais
    }
  };
  
  useEffect(() => {
    if (id) {
      fetchGameDetails();
    }
  }, [id, token]);
  
  if (!id) {
    return <div>Game not found</div>;
  }
  
  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary mx-auto"></div>
          <p className="mt-2 text-text-secondary">Carregando detalhes do jogo...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <h2 className="text-lg font-semibold text-status-error-text mb-2">Erro ao carregar jogo</h2>
        <p className="text-text-secondary">{error}</p>
      </div>
    );
  }
  
  if (!game) {
    return (
      <div className="p-6 text-center">
        <p className="text-text-secondary">Jogo não encontrado</p>
      </div>
    );
  }

  // Combinar game data com participants
  const gameWithParticipants = {
    ...game,
    participants: participants.length > 0 ? participants : undefined
  };

  return (
    <div className="p-6">
      <GameDetails 
        game={gameWithParticipants}
        isActionLoading={actionLoading}
        onShare={shareGame}
        onJoinGame={async (gameId) => {
          if (actionLoading) return; // Evita múltiplas ações simultâneas
          
          setActionLoading('join');
          try {
            console.log('🎯 Joining game:', gameId);
            
            // Atualização otimista: atualizar userStatus no game
            setGame(prev => prev ? { 
              ...prev, 
              userStatus: ParticipationStatus.CONFIRMED,
              occupiedSlots: prev.occupiedSlots + 1 
            } : prev);
            
            await gameService.joinGame(gameId);
            toastManager.success('🎯 Solicitação de participação enviada!');
            
            // Recarregar dados para atualizar a lista de participantes
            await refreshGameData();
            
          } catch (error) {
            console.error('❌ Error joining game:', error);
            toastManager.error(error instanceof Error ? error.message : 'Erro ao solicitar participação');
            
            // Reverter em caso de erro
            await fetchGameDetails();
          } finally {
            setActionLoading(null);
          }
        }}
        onConfirmParticipation={async (gameId) => {
          if (actionLoading) return;
          
          setActionLoading('confirm');
          const originalUserStatus = game?.userStatus;
          
          try {
            console.log('✅ Confirming participation:', gameId);
            
            // Atualização otimista
            setGame(prev => prev ? { 
              ...prev, 
              userStatus: ParticipationStatus.CONFIRMED,
              occupiedSlots: prev.userStatus !== ParticipationStatus.CONFIRMED ? prev.occupiedSlots + 1 : prev.occupiedSlots
            } : prev);
            
            await gameService.confirmParticipation(gameId);
            toastManager.success('✅ Participação confirmada com sucesso!');
            
            // Recarregar dados para atualizar a lista de participantes
            await refreshGameData();
            
          } catch (error) {
            console.error('❌ Error confirming participation:', error);
            toastManager.error(error instanceof Error ? error.message : 'Erro ao confirmar participação');
            
            // Reverter em caso de erro
            if (originalUserStatus && game) {
              setGame(prev => prev ? { ...prev, userStatus: originalUserStatus } : prev);
            }
          } finally {
            setActionLoading(null);
          }
        }}
        onDeclineInvitation={async (gameId) => {
          if (actionLoading) return;
          
          setActionLoading('decline');
          const originalUserStatus = game?.userStatus;
          
          try {
            console.log('❌ Declining invitation:', gameId);
            
            // Atualização otimista
            setGame(prev => prev ? { 
              ...prev, 
              userStatus: ParticipationStatus.DECLINED 
            } : prev);
            
            await gameService.cancelParticipation(gameId);
            toastManager.success('❌ Convite recusado');
            
            // Recarregar dados para atualizar a lista de participantes
            await refreshGameData();
            
          } catch (error) {
            console.error('❌ Error declining invitation:', error);
            toastManager.error(error instanceof Error ? error.message : 'Erro ao recusar convite');
            
            // Reverter em caso de erro
            if (originalUserStatus && game) {
              setGame(prev => prev ? { ...prev, userStatus: originalUserStatus } : prev);
            }
          } finally {
            setActionLoading(null);
          }
        }}
        onCancelParticipation={async (gameId) => {
          if (actionLoading) return;
          
          setActionLoading('cancel');
          const originalUserStatus = game?.userStatus;
          
          try {
            console.log('🚫 Canceling participation:', gameId);
            
            // Atualização otimista
            setGame(prev => prev ? { 
              ...prev, 
              userStatus: ParticipationStatus.CANCELED,
              occupiedSlots: prev.userStatus === ParticipationStatus.CONFIRMED ? prev.occupiedSlots - 1 : prev.occupiedSlots
            } : prev);
            
            await gameService.cancelParticipation(gameId);
            toastManager.success('🚫 Participação cancelada');
            
            // Recarregar dados para atualizar a lista de participantes
            await refreshGameData();
            
          } catch (error) {
            console.error('❌ Error canceling participation:', error);
            toastManager.error(error instanceof Error ? error.message : 'Erro ao cancelar participação');
            
            // Reverter em caso de erro
            if (originalUserStatus && game) {
              setGame(prev => prev ? { ...prev, userStatus: originalUserStatus } : prev);
            }
          } finally {
            setActionLoading(null);
          }
        }}
        onManageParticipant={async (gameId, participantId, action) => {
          if (actionLoading) return;
          
          setActionLoading(`manage-${participantId}-${action}`);
          
          try {
            console.log('🛠️ Managing participant:', gameId, participantId, action);
            
            // Fazer chamada à API
            await gameService.manageParticipant(gameId, participantId, action);
            toastManager.success(`🛠️ Participante ${action === 'approve' ? 'aprovado' : action === 'reject' ? 'rejeitado' : 'removido'} com sucesso`);
            
            // Recarregar dados silenciosamente (sem loading indicators)
            await refreshGameData();
            
          } catch (error) {
            console.error('❌ Error managing participant:', error);
            toastManager.error(error instanceof Error ? error.message : 'Erro ao gerenciar participante');
          } finally {
            setActionLoading(null);
          }
        }}
        onCancelGame={async (gameId) => {
          if (actionLoading) return;
          
          setActionLoading('cancelGame');
          try {
            console.log('🚫 Canceling game:', gameId);
            // TODO: Implementar endpoint de cancelar jogo quando disponível na API
            console.warn('Cancel game endpoint not yet available');
            toastManager.error('Funcionalidade de cancelar jogo ainda não disponível');
          } catch (error) {
            console.error('❌ Error canceling game:', error);
            toastManager.error(error instanceof Error ? error.message : 'Erro ao cancelar jogo');
          } finally {
            setActionLoading(null);
          }
        }}
        onInviteUser={async (gameId, userId) => {
          if (actionLoading) return;
          
          setActionLoading(`invite-${userId}`);
          try {
            console.log('👥 Inviting user:', gameId, userId);
            
            await gameService.inviteUserToGame(gameId, userId);
            toastManager.success('👥 Usuário convidado com sucesso!');
            
            // Recarregar dados para atualizar a lista de participantes
            await refreshGameData();
            
          } catch (error) {
            console.error('❌ Error inviting user:', error);
            toastManager.error(error instanceof Error ? error.message : 'Erro ao convidar usuário');
          } finally {
            setActionLoading(null);
          }
        }}
        onInviteGuest={async (gameId, name, phoneNumber) => {
          if (actionLoading) return;
          
          setActionLoading('invite-guest');
          try {
            console.log('👤 Inviting guest:', gameId, name, phoneNumber);
            
            await gameService.inviteGuestToGame(gameId, name, phoneNumber);
            toastManager.success('👤 Convidado convidado com sucesso!');
            
            // Recarregar participantes para mostrar o novo convidado
            await refreshGameData();
            
          } catch (error) {
            console.error('❌ Error inviting guest:', error);
            toastManager.error(error instanceof Error ? error.message : 'Erro ao convidar convidado');
          } finally {
            setActionLoading(null);
          }
        }}
      />
    </div>
  );
}